import {
  EuroCircleOutlined,
  FileOutlined,
  LoadingOutlined,
  UserOutlined,
  SwapOutlined,
  DeleteOutlined,
  CopyOutlined,
} from "@ant-design/icons"
import {
  Button,
  Col,
  Divider,
  Flex,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Alert,
  Modal,
} from "antd"
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
  deleteDoc,
  setDoc,
} from "firebase/firestore"
import React, { useEffect, useState } from "react"
import { TableSearch } from "../../../components/utils/TableSearch"
import { db } from "../../../firebase/config"
import { useCollection } from "../../../hooks/useCollection"
import moment from "moment"
import EditClientDrawer from "../../../components/Clients/EditClientDrawer"
import HandleOrderCredit from "../../Accounting/Clients/Helper/HandleOrderCredit"
import * as XLSX from "xlsx"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Title, Text, Link } = Typography

export default function AllClients() {
  const { data: manufacturerData, loading: manufacturerLoading } = useCollection("manufacturers")
  const { user } = useAuthContext()
  const [priceTableData, setPriceTableData] = useState(null)
  const [tableLoading, setTableLoading] = useState(false)
  const [orderCounts, setOrderCounts] = useState({})
  const [orderProfits, setOrderProfits] = useState({})
  const [orderTypeBreakdown, setOrderTypeBreakdown] = useState({})
  const [filterIgnoredClients, setFilterIgnoredClients] = useState(true)
  const [selectedPercentage, setSelectedPercentage] = useState(0)
  const [clientsLoaded, setClientsLoaded] = useState(false)
  const [ordersLoaded, setOrdersLoaded] = useState(false)
  const [extraDelayComplete, setExtraDelayComplete] = useState(false)
  const [selectBoxesLoading, setSelectBoxesLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [editingCreditUsedId, setEditingCreditUsedId] = useState(null)
  const [editingCreditUsedValue, setEditingCreditUsedValue] = useState(null)

  // Transfer states
  const [salesUsers, setSalesUsers] = useState([])
  const [selectedSalesUser, setSelectedSalesUser] = useState(null)
  const [transferLoading, setTransferLoading] = useState(false)

  // Fetch sales team users
  useEffect(() => {
    const usersQuery = query(collection(db, "users"), where("group", "array-contains", "sales"))
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const users = []
      snapshot.docs.forEach((doc) => {
        users.push({ ...doc.data(), id: doc.id })
      })
      setSalesUsers(users)
    })
    return () => unsubscribe()
  }, [])

  // Fetch clients
  useEffect(() => {
    setTableLoading(true)
    const priceRef = query(collection(db, "clients"), where("approved", "==", true))
    const ignoredClientUids = ["E1XNaWvT6PccXRSKB3JNlNehI362"]
    const hiddenEmailPatterns = ["mpdruka@pprintgroup.com"]
    const allowedUserIdForHidden = "a8VctPNqlDPDPCd1k7BhXczYobt2"

    const unsubscribe = onSnapshot(priceRef, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const client = doc.data()

        // Hide clients with specific emails unless current user has specific ID
        if (hiddenEmailPatterns.includes(client.userEmail)) {
          if (user.uid !== allowedUserIdForHidden) {
            return
          }
        }

        if (filterIgnoredClients && ignoredClientUids.includes(client.user)) return
        results.push({ ...client, id: doc.id })
      })
      setPriceTableData(results)
      setClientsLoaded(true)
      setTableLoading(false)
    })
    return () => unsubscribe()
  }, [filterIgnoredClients, user.uid])

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys)
      setSelectedRows(newSelectedRows)
    },
  }

  // Fetch orders
  useEffect(() => {
    const ordersQuery = query(collection(db, "orders"))
    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      const counts = {}
      const profits = {}
      const typeBreakdown = {}
      snapshot.docs.forEach((doc) => {
        const order = doc.data()
        const clientId = order.clientId
        if (clientId) {
          counts[clientId] = (counts[clientId] || 0) + 1
          let orderProfit = 0
          if (order.selectedProduct && order.selectedProduct.machine === "Tape") {
            orderProfit = (order.rollCount || 0) * (order.price || 0)
          } else {
            orderProfit = (order.labelCount || 0) * (order.price || 0)
          }
          profits[clientId] = (profits[clientId] || 0) + orderProfit
          if (!typeBreakdown[clientId]) {
            typeBreakdown[clientId] = { tape: 0, label: 0 }
          }
          if (order.selectedProduct && order.selectedProduct.machine === "Tape") {
            typeBreakdown[clientId].tape += 1
          } else {
            typeBreakdown[clientId].label += 1
          }
        }
      })
      setOrderCounts(counts)
      setOrderProfits(profits)
      setOrderTypeBreakdown(typeBreakdown)
      setOrdersLoaded(true)
    })
    return () => unsubscribeOrders()
  }, [])

  // Extra Delay
  useEffect(() => {
    if (clientsLoaded && ordersLoaded && priceTableData) {
      const timer = setTimeout(() => {
        setExtraDelayComplete(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [clientsLoaded, ordersLoaded, priceTableData])

  // Reusable batch updater
  const batchUpdateDocs = async (snapshot, collectionName, fields) => {
    if (snapshot.empty) return 0

    let batch = writeBatch(db)
    let writesCount = 0
    let totalWrites = 0

    snapshot.forEach((d) => {
      batch.update(doc(db, collectionName, d.id), fields)
      writesCount++
      totalWrites++

      if (writesCount === 500) {
        batch.commit()
        batch = writeBatch(db)
        writesCount = 0
      }
    })

    if (writesCount > 0) await batch.commit()
    return totalWrites
  }

  // Transfer clients + all related documents
  const handleTransferClients = async () => {
    if (!selectedSalesUser) {
      message.warning("Please select a sales manager first")
      return
    }
    if (selectedRowKeys.length === 0) {
      message.warning("Please select clients to transfer")
      return
    }

    setTransferLoading(true)

    try {
      const selectedUser = salesUsers.find((u) => u.id === selectedSalesUser)

      if (!selectedUser || !selectedUser.email) {
        message.error("Selected user data is invalid")
        return
      }

      // Fields shared across clients, priceCalculation, tapePrices
      const baseFields = {
        user: selectedUser.id,
        userEmail: selectedUser.email,
      }

      // Extra fields only on orders (uid + nested snapshots)
      const orderFields = {
        ...baseFields,
        uid: selectedUser.id,
        "client.user": selectedUser.id,
        "client.userEmail": selectedUser.email,
        "selectedProduct.user": selectedUser.id,
        "selectedProduct.userEmail": selectedUser.email,
        "userData.salesManager": selectedUser.id,
        "userData.salesEmail": selectedUser.email,
      }

      let totalUpdates = 0

      for (const clientId of selectedRowKeys) {
        // 1. Client document
        await updateDoc(doc(db, "clients", clientId), baseFields)
        totalUpdates++

        // 2. priceCalculation
        const priceCalcSnapshot = await getDocs(
          query(collection(db, "priceCalculation"), where("clientId", "==", clientId)),
        )
        totalUpdates += await batchUpdateDocs(priceCalcSnapshot, "priceCalculation", baseFields)

        // 3. tapePrices
        const tapePricesSnapshot = await getDocs(
          query(collection(db, "tapePrices"), where("clientId", "==", clientId)),
        )
        totalUpdates += await batchUpdateDocs(tapePricesSnapshot, "tapePrices", baseFields)

        // 4. orders — includes all nested user references
        const ordersSnapshot = await getDocs(
          query(collection(db, "orders"), where("clientId", "==", clientId)),
        )
        totalUpdates += await batchUpdateDocs(ordersSnapshot, "orders", orderFields)
      }

      message.success(
        `Transferred ${selectedRowKeys.length} client(s) — ${totalUpdates} total records updated → ${selectedUser.email}`,
      )

      setSelectedRowKeys([])
      setSelectedRows([])
      setSelectedSalesUser(null)
    } catch (error) {
      console.error("Error transferring clients:", error)
      message.error("Failed to transfer clients. Check console for details.")
    } finally {
      setTransferLoading(false)
    }
  }

  // Delete selected clients and all related prices
  const handleDeleteClients = () => {
    Modal.confirm({
      title: "Delete Clients",
      content: (
        <div>
          <p>
            Are you sure you want to delete <strong>{selectedRowKeys.length}</strong> client(s)?
          </p>
          <p style={{ color: "red", marginBottom: 0 }}>
            <strong>Warning:</strong> This will permanently delete:
            <ul style={{ marginTop: 8 }}>
              <li>Client records</li>
              <li>All priceCalculations for these clients</li>
              <li>All tapePrices for these clients</li>
            </ul>
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setTransferLoading(true)
        try {
          let totalDeleted = 0

          for (const clientId of selectedRowKeys) {
            // 1. Delete client document
            await deleteDoc(doc(db, "clients", clientId))
            totalDeleted++

            // 2. Delete all priceCalculations for this client
            const priceCalcSnapshot = await getDocs(
              query(collection(db, "priceCalculation"), where("clientId", "==", clientId)),
            )
            let batch = writeBatch(db)
            let batchCount = 0
            priceCalcSnapshot.forEach((d) => {
              batch.delete(doc(db, "priceCalculation", d.id))
              batchCount++
              totalDeleted++

              if (batchCount === 500) {
                batch.commit()
                batch = writeBatch(db)
                batchCount = 0
              }
            })
            if (batchCount > 0) await batch.commit()

            // 3. Delete all tapePrices for this client
            const tapePricesSnapshot = await getDocs(
              query(collection(db, "tapePrices"), where("clientId", "==", clientId)),
            )
            batch = writeBatch(db)
            batchCount = 0
            tapePricesSnapshot.forEach((d) => {
              batch.delete(doc(db, "tapePrices", d.id))
              batchCount++
              totalDeleted++

              if (batchCount === 500) {
                batch.commit()
                batch = writeBatch(db)
                batchCount = 0
              }
            })
            if (batchCount > 0) await batch.commit()
          }

          message.success(
            `Deleted ${selectedRowKeys.length} client(s) — ${totalDeleted} total records removed`,
          )

          setSelectedRowKeys([])
          setSelectedRows([])
        } catch (error) {
          console.error("Error deleting clients:", error)
          message.error("Failed to delete clients. Check console for details.")
        } finally {
          setTransferLoading(false)
        }
      },
      onCancel: () => {
        // User cancelled the deletion
      },
    })
  }

  const handleDuplicateSelectedClients = () => {
    Modal.confirm({
      title: "Duplicate Selected Clients",
      width: 600,
      content: (
        <div>
          <p>
            Duplicate <strong>{selectedRowKeys.length}</strong> client(s) for which sales user?
          </p>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Select Sales User:
            </Text>
            <Select
              placeholder="Choose a sales user..."
              style={{ width: "100%" }}
              value={selectedSalesUser}
              onChange={setSelectedSalesUser}
              options={salesUsers.map((user) => ({
                value: user.id,
                label: user.email,
              }))}
              optionFilterProp="label"
              showSearch
            />
          </div>
          <p style={{ marginBottom: 0, fontSize: 12, color: "#666" }}>
            This will create copies of selected clients with:
            <ul style={{ marginTop: 8 }}>
              <li>New client names with " - Copy" suffix</li>
              <li>Assigned to selected sales user</li>
              <li>No prices (you can create new prices for copied clients)</li>
            </ul>
          </p>
        </div>
      ),
      okText: "Duplicate",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        if (!selectedSalesUser) {
          message.error("Please select a sales user")
          return
        }

        setTransferLoading(true)
        try {
          const selectedUser = salesUsers.find((u) => u.id === selectedSalesUser)

          for (const clientId of selectedRowKeys) {
            const clientData = selectedRows.find((r) => r.id === clientId)
            if (!clientData) continue

            // Create new client document (without prices)
            const newClientId = doc(collection(db, "clients")).id
            const newClientData = {
              ...clientData,
              client: `${clientData.client} - Copy`,
              id: newClientId,
              user: selectedSalesUser,
              userEmail: selectedUser.email,
            }
            delete newClientData.key // Remove table key
            await setDoc(doc(db, "clients", newClientId), newClientData)
          }

          message.success(
            `Duplicated ${selectedRowKeys.length} client(s) for ${selectedUser.email}`,
          )
          setSelectedRowKeys([])
          setSelectedRows([])
          setSelectedSalesUser(null)
        } catch (error) {
          console.error("Error duplicating clients:", error)
          message.error("Failed to duplicate clients. Check console for details.")
        } finally {
          setTransferLoading(false)
        }
      },
      onCancel: () => {
        // User cancelled
      },
    })
  }

  // updateCreditUsed function
  const updateCreditUsed = async (value, id, clientData) => {
    const ref = doc(db, "clients", id)
    const creditUsedNum = Number(value) || 0
    const creditLimit = Number(clientData.creditLimit) || 0
    const creditLeft = Math.round((creditLimit - creditUsedNum) * 100) / 100

    try {
      await updateDoc(ref, {
        creditUsed: creditUsedNum,
        creditLeft: creditLeft,
      })
      message.success("Credit updated!")
      setEditingCreditUsedId(null)
      setEditingCreditUsedValue(null)
    } catch (error) {
      console.error("Error updating credit:", error)
      message.error("Failed to update credit.")
    }
  }

  // updatePercentage function
  const updatePercentage = async (value, id) => {
    const ref = doc(db, "clients", id)
    try {
      await updateDoc(ref, { profitability: value })
      message.success("Updated!")
      setSelectedPercentage(0)
    } catch (error) {
      console.error("Error updating percentage:", error)
      message.error("Failed to update percentage.")
    }
  }

  // percentageContent function
  const percentageContent = (currentValue, id) => {
    return (
      <Space>
        <Input
          defaultValue={currentValue}
          value={selectedPercentage}
          onChange={(event) => setSelectedPercentage(event.target.value)}
          placeholder="New %"
        />
        <Button onClick={() => updatePercentage(selectedPercentage, id)} type="primary">
          Update
        </Button>
      </Space>
    )
  }

  // handleManufacturer function
  const handleManufacturer = async (value, option, record) => {
    setSelectBoxesLoading(true)
    const clientRef = doc(db, "clients", record.id)
    try {
      const manufacturerObject = manufacturerData?.find((m) => m.company === value)
      if (!manufacturerObject) {
        message.error("Manufacturer details not found.")
        setSelectBoxesLoading(false)
        return
      }
      await updateDoc(clientRef, {
        manufacturer: { ...manufacturerObject },
      })

      const ordersSnapshot = await getDocs(
        query(collection(db, "orders"), where("clientId", "==", record.id)),
      )

      if (!ordersSnapshot.empty) {
        let batch = writeBatch(db)
        let writesCount = 0
        ordersSnapshot.forEach((orderDoc) => {
          batch.update(doc(db, "orders", orderDoc.id), {
            "client.manufacturer": { ...manufacturerObject },
            manufacturer: manufacturerObject,
          })
          writesCount++
          if (writesCount === 500) {
            batch.commit()
            batch = writeBatch(db)
            writesCount = 0
          }
        })
        if (writesCount > 0) await batch.commit()
      }

      message.success("Manufacturer updated!")
    } catch (error) {
      message.error("Error updating manufacturer. Check log.")
      console.error("Manufacturer update error:", error)
    } finally {
      setSelectBoxesLoading(false)
    }
  }

  // exportToExcel function
  const exportToExcel = () => {
    const hiddenEmailPatterns = ["mpdruka@pprintgroup.com"]
    const allowedUserIdForHidden = "a8VctPNqlDPDPCd1k7BhXczYobt2"

    let exportRows = selectedRows.length > 0 ? selectedRows : priceTableData

    // Filter out hidden email clients unless user has allowed ID
    if (user.uid !== allowedUserIdForHidden) {
      exportRows = exportRows.filter((row) => !hiddenEmailPatterns.includes(row.userEmail))
    }

    if (!exportRows || exportRows.length === 0) {
      message.warning("No data to export.")
      return
    }
    const exportData = exportRows.map((row) => {
      const breakdown = orderTypeBreakdown[row.id]
      let orderType = "No Orders"
      if (breakdown) {
        const total = breakdown.tape + breakdown.label
        if (total > 0) {
          const tapePercent = Math.round((breakdown.tape / total) * 100)
          const labelPercent = Math.round((breakdown.label / total) * 100)
          orderType = `Tape ${tapePercent}%, Label ${labelPercent}%`
        }
      }
      return {
        Client: row.client,
        VAT: row.vat,
        REG: row.reg,
        "Contact Name": row.contactName,
        "Order Limit": row.creditLimit,
        Manufacturer: row.manufacturer?.company || "",
        Manager: row.userEmail,
        "Order Count": orderCounts[row.id] || 0,
        "Profit (€)": orderProfits[row.id] ? Math.floor(orderProfits[row.id]) : 0,
        "Order Type": orderType,
        Created: row.date?.createdAt
          ? moment.unix(row.date.createdAt.seconds).format("YYYY-MM-DD")
          : "",
        "Profitability (%)": row.profitability,
        "Credit Used": row.creditUsed,
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 12 },
      { wch: 20 },
      { wch: 25 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 12 },
      { wch: 15 },
      { wch: 12 },
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients")
    XLSX.writeFile(workbook, "clients_export.xlsx")
  }

  // --- Filters ---
  const manufacturerFilter = manufacturerData
    ? [...new Map(manufacturerData.map((item) => [item.company, item])).values()].map((item) => ({
        text: item.company,
        value: item.company,
      }))
    : []

  const managerFilter = priceTableData
    ? [...new Set(priceTableData.map((client) => client.userEmail).filter(Boolean))].map(
        (email) => ({ text: email, value: email }),
      )
    : []

  // --- Columns Definition ---
  const columns = [
    {
      title: "Clients",
      dataIndex: "client",
      width: 200,
      ...TableSearch("client"),
      ellipsis: { showTitle: false },
      render: (client) => <Tooltip title={client}>{client}</Tooltip>,
    },
    {
      title: "VAT",
      dataIndex: "vat",
      width: 120,
      ellipsis: { showTitle: false },
      render: (vat) => <Tooltip title={vat}>{vat}</Tooltip>,
    },
    {
      title: "Contact",
      dataIndex: "contactName",
      width: 150,
      ellipsis: { showTitle: false },
      render: (contact) => <Tooltip title={contact}>{contact}</Tooltip>,
    },
    {
      title: "Manager",
      dataIndex: "userEmail",
      width: 180,
      filters: managerFilter,
      ellipsis: { showTitle: false },
      render: (email) => <Tooltip title={email}>{email}</Tooltip>,
      onFilter: (value, record) => record.userEmail === value,
    },
    {
      title: "Orders",
      align: "center",
      width: 80,
      sorter: (a, b) => (orderCounts[a.id] || 0) - (orderCounts[b.id] || 0),
      render: (_, record) => <span>{orderCounts[record.id] || 0}</span>,
    },
    {
      title: "Order Type",
      width: 150,
      align: "center",
      render: (_, record) => {
        const breakdown = orderTypeBreakdown[record.id]
        if (!breakdown || (breakdown.tape === 0 && breakdown.label === 0)) {
          return <Tag color="red">No Orders</Tag>
        }
        const total = breakdown.tape + breakdown.label
        const tapePercent = Math.round((breakdown.tape / total) * 100)
        const labelPercent = Math.round((breakdown.label / total) * 100)
        return (
          <Space direction="vertical" size="small">
            {breakdown.tape > 0 && <Tag color="blue">Tape {tapePercent}%</Tag>}
            {breakdown.label > 0 && <Tag color="green">Label {labelPercent}%</Tag>}
          </Space>
        )
      },
    },
    {
      title: "Profit",
      align: "center",
      width: 100,
      sorter: (a, b) => (orderProfits[a.id] || 0) - (orderProfits[b.id] || 0),
      render: (_, record) => {
        const profit = orderProfits[record.id] || 0
        const formatted = Math.floor(profit).toLocaleString()
        const color = profit <= 0 ? "red" : "green"
        return <span style={{ color, fontSize: "12px" }}>{formatted} eur</span>
      },
    },
    {
      title: "Details",
      align: "center",
      width: 80,
      render: (_, record) => <EditClientDrawer data={record} />,
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      width: 150,
      filters: manufacturerFilter,
      onFilter: (value, record) => record.manufacturer?.company === value,
      render: (company, record) => (
        <Select
          fieldNames={{ label: "company", value: "company" }}
          loading={manufacturerLoading || selectBoxesLoading}
          value={company}
          onChange={(value, option) => handleManufacturer(value, option, record)}
          style={{ width: "100%" }}
          options={manufacturerData}
          placeholder="Select..."
          allowClear
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: "Created",
      dataIndex: ["date", "createdAt"],
      align: "center",
      width: 100,
      sorter: (a, b) => a.date?.createdAt?.seconds - b.date?.createdAt?.seconds,
      render: (value) =>
        value ? <Text>{moment.unix(value.seconds).format("DD/MM/YY")}</Text> : "-",
    },
    {
      title: "%",
      dataIndex: "profitability",
      align: "center",
      width: 80,
      sorter: (a, b) => (a.profitability || 0) - (b.profitability || 0),
      render: (value, record) => (
        <Popover
          key={record.id}
          trigger="click"
          content={() => percentageContent(value, record.id)}
          title="Update Profitability %">
          <Link>{value != null ? `${value} %` : "-"}</Link>
        </Popover>
      ),
    },
    {
      title: "Credit Used",
      width: 150,
      align: "center",
      render: (_, record) => {
        if (editingCreditUsedId === record.id) {
          return (
            <Space size="small">
              <Input
                type="number"
                value={editingCreditUsedValue}
                onChange={(e) => setEditingCreditUsedValue(e.target.value)}
                placeholder="Amount"
                style={{ width: 80 }}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => updateCreditUsed(editingCreditUsedValue, record.id, record)}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditingCreditUsedId(null)}>
                Cancel
              </Button>
            </Space>
          )
        }
        return (
          <Text
            italic
            onClick={() => {
              setEditingCreditUsedId(record.id)
              setEditingCreditUsedValue(record.creditUsed || 0)
            }}
            style={{ cursor: "pointer", color: "#1677ff" }}>
            {record.creditUsed?.toLocaleString() ?? 0} / {record.creditLimit?.toLocaleString() ?? 0}
          </Text>
        )
      },
    },
    {
      title: "C Limit",
      dataIndex: "creditLimit",
      align: "center",
      width: 100,
      sorter: (a, b) => (a.creditLimit || 0) - (b.creditLimit || 0),
      render: (_, record) => <HandleOrderCredit client={record} />,
      fixed: "right",
    },
  ]

  // --- Loading State ---
  let spinTip = "Loading data, please wait..."
  if (!clientsLoaded) spinTip = "Loading clients..."
  else if (!ordersLoaded) spinTip = "Calculating order details..."
  else if (!extraDelayComplete) spinTip = "Finalizing data..."

  const allDataLoaded = clientsLoaded && ordersLoaded && priceTableData && extraDelayComplete

  if (!allDataLoaded) {
    return (
      <Flex vertical justify="center" align="center" gap="middle" style={{ minHeight: "50vh" }}>
        <LoadingOutlined spin style={{ fontSize: 48, color: "#1677ff" }} />
        <Title level={3}>{spinTip}</Title>
      </Flex>
    )
  }

  // --- Render ---
  return (
    <div style={{ padding: "15px 20px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: "15px" }}>
        <Col>
          <Space size="middle">
            <Title level={3} style={{ margin: 0 }}>
              <UserOutlined />
              <Divider type="vertical" />
              All Clients ({priceTableData?.length ?? 0})
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<FileOutlined />}
              type="primary"
              onClick={exportToExcel}
              disabled={!priceTableData || priceTableData.length === 0}>
              Export Excel
            </Button>
            {["E1XNaWvT6PccXRSKB3JNlNehI362"].includes(user.uid) && (
              <Button onClick={() => setFilterIgnoredClients(!filterIgnoredClients)}>
                {filterIgnoredClients ? "Show Ignored" : "Hide Ignored"}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Transfer Bar — appears when rows are selected */}
      {selectedRowKeys.length > 0 && (
        <Alert
          message={
            <Row gutter={16} align="middle">
              <Col>
                <Text strong>
                  {selectedRowKeys.length} client(s) selected:
                </Text>
              </Col>
              <Col>
                <Select
                  style={{ width: 300 }}
                  placeholder="Select Sales Manager"
                  value={selectedSalesUser}
                  onChange={setSelectedSalesUser}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={salesUsers.map((salesUser) => ({
                    label: `${salesUser.name || "Unknown"} (${salesUser.email})`,
                    value: salesUser.id,
                  }))}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={handleTransferClients}
                  loading={transferLoading}
                  disabled={!selectedSalesUser}>
                  Transfer Clients & Prices
                </Button>
              </Col>
              <Col>
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleDuplicateSelectedClients}
                  loading={transferLoading}>
                  Duplicate Selected
                </Button>
              </Col>
              <Col>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteClients}
                  loading={transferLoading}>
                  Delete Clients & Prices
                </Button>
              </Col>
              <Col>
                <Button
                  onClick={() => {
                    setSelectedRowKeys([])
                    setSelectedRows([])
                    setSelectedSalesUser(null)
                  }}>
                  Cancel
                </Button>
              </Col>
            </Row>
          }
          type="info"
          style={{ marginBottom: 15 }}
          showIcon
        />
      )}

      <Table
        rowSelection={rowSelection}
        tableLayout="fixed"
        dataSource={priceTableData}
        size="small"
        columns={columns}
        loading={tableLoading}
        rowKey={(record) => record.id}
        pagination={false}
        scroll={{
          y: "calc(100vh - 280px)",
          x: 1800,
        }}
        bordered
      />
    </div>
  )
}
